/**
 * ArchLang Graph Engine (v0.2)
 *
 * Derives the dependency graph from a `ResolvedModel`:
 *   - Process steps produce caller-module → callee-interface edges.
 *     Events ride the same path: a consumer reaches a producer's
 *     async-kind handler interface through a normal `>` step.
 *
 * Plus indexes for fast lookup:
 *   - modulesByPath:    qualified-name → resolved module
 *   - interfacesByPath: qualified-name → resolved interface
 *   - modulesById:      stable id → resolved module
 *   - surfacesByPath:     qualified-name → resolved surface (useful for
 *     diagrams that draw surfaces as clusters)
 */

import type {
  ProcessDecl,
  QualifiedName,
  SourceSpan,
  StableId,
  SubprocessDecl,
} from "./types.js";
import { processQualifiedName, processIdentity } from "./types.js";
import { buildSpliceScope } from "./splice-scope.js";
import type { CallHop, Flow, ProcessModel, SubprocessModel } from "./process-model.js";
import { emitSteps } from "./process-step-emitter.js";
import type {
  ResolvedSurface,
  ResolvedInterface,
  ResolvedModel,
  ResolvedModule,
} from "./resolver.js";
import {
  moduleSuffixes,
  ifaceSpellings,
  buildScopedModuleIndex,
  resolveScoped,
  spaceOfModule,
  type ScopedCand,
} from "./hoist-index.js";

// =============================================================================
// Edge & graph types
// =============================================================================

export type EdgeSource = "process";

/** The edge-universe discriminant (design v0.10 §1, selector `@kind`):
 *  `call` = module → interface invocation (the historical edge); `do` =
 *  process → spliced subprocess/process (a resolved `do` step). `go` re-entry
 *  arcs are deliberately NOT edges. */
export type EdgeKind = "call" | "do";

/** Fields every derived edge carries — provenance + the deriving process's
 *  IDENTITY (a `#id` when present, else a content key for an anonymous process;
 *  see `processIdentity`). For a `do` edge the attribution is the ROOT process,
 *  matching call edges inlined from the same splice, so `in this` sees the whole
 *  splice-inclusive action set (design §1). */
interface EdgeCommon {
  readonly source: EdgeSource;
  readonly processId?: string;
  readonly processName?: string;
  /** Step index within the process (depth-first ordering). */
  readonly stepIndex?: number;
  /** Branch / case / catch / unwind label in scope, if any. */
  readonly altCondition?: string;
  readonly span?: SourceSpan;
}

/** A caller-module → callee-interface invocation edge (`@kind = call`). */
export interface CallEdge extends EdgeCommon {
  readonly kind: "call";
  /** Source module path (e.g., ["Payments"]). */
  readonly fromModulePath: readonly string[];
  readonly fromModuleId?: StableId;
  /** Module that owns the target interface (e.g., ["Inventory"]). */
  readonly toModulePath: readonly string[];
  readonly toModuleId?: StableId;
  /** Full path to the target interface (e.g., ["Inventory", "Reserve"]). */
  readonly toInterfacePath: readonly string[];
  /** v0.11 (D2a): each endpoint's LANDED identity space — module paths alone
   *  cannot distinguish two same-named roots in sibling spaces. Undefined at
   *  package root / spaceless models (byte-stable). */
  readonly fromSpace?: QualifiedName;
  readonly toSpace?: QualifiedName;
  /** True for a saga compensation edge — the `unwind` reverse call of a
   *  `reversible` step. A typed flag so board consumers can style/filter it
   *  without string-matching `altCondition === "unwind"`. */
  readonly compensation?: boolean;
}

/** A process → spliced subprocess/process invocation edge (`@kind = do`).
 *  Endpoints are process IDENTITIES (never modules), so a process ref in a
 *  selector matches `do` edges only (design §1). `from` is the immediate
 *  invoker (the enclosing splice's target, or the root process at depth 0);
 *  `to` is the spliced target. */
export interface DoEdge extends EdgeCommon {
  readonly kind: "do";
  readonly fromProcessId: string;
  readonly fromProcessName?: string;
  readonly toProcessId: string;
  readonly toProcessName?: string;
  /** True when the spliced target is a `subprocess` (vs a full `process`). */
  readonly targetIsSubprocess?: boolean;
}

export type DependencyEdge = CallEdge | DoEdge;

/** Narrow to the module→interface call edges — the historical universe most
 *  consumers (board render, blast radius, module diff) still work over. */
export const isCallEdge = (e: DependencyEdge): e is CallEdge => e.kind === "call";
export const isDoEdge = (e: DependencyEdge): e is DoEdge => e.kind === "do";

export interface DependencyGraph {
  readonly edges: readonly DependencyEdge[];
  readonly modulesByPath: ReadonlyMap<string, ResolvedModule>;
  readonly interfacesByPath: ReadonlyMap<string, ResolvedInterface>;
  readonly surfacesByPath: ReadonlyMap<string, ResolvedSurface>;
  readonly modulesById: ReadonlyMap<StableId, ResolvedModule>;
  /**
   * Reverse of {@link modulesByPath}: each resolved module → its
   * qualified path as an array. Authoritative source for module → path
   * conversion; callers should prefer this over walking `modulesByPath`
   * or reading `ResolvedModule.parentPath` (which the resolver strips
   * during re-parenting).
   */
  readonly pathOfModule: ReadonlyMap<ResolvedModule, readonly string[]>;
}

const EMPTY_GRAPH: DependencyGraph = {
  edges: [],
  modulesByPath: new Map(),
  interfacesByPath: new Map(),
  surfacesByPath: new Map(),
  modulesById: new Map(),
  pathOfModule: new Map(),
};

// =============================================================================
// Public API
// =============================================================================

export function buildGraph(model: ResolvedModel): DependencyGraph {
  if (!model || model.modules.length === 0) {
    if (model.processes.length === 0) return EMPTY_GRAPH;
  }

  const indexes = buildIndexes(model);
  const edges = buildEdges(model, indexes);

  return {
    edges,
    modulesByPath: indexes.modulesByPath,
    interfacesByPath: indexes.interfacesByPath,
    surfacesByPath: indexes.surfacesByPath,
    modulesById: indexes.modulesById,
    pathOfModule: indexes.pathOfModule,
  };
}

/**
 * Compute the dependency EDGES from a model + its prebuilt indexes. Edges are
 * a pure function of the processes + the structural path index — NOT of field
 * values. The incremental compiler exploits this: a value edit doesn't change
 * any path/process, so the edges are reused verbatim (only the indexes are
 * refreshed to point at the new module objects). Exported for that reuse.
 */
export function buildEdges(model: ResolvedModel, indexes: GraphIndexes): DependencyEdge[] {
  const edges: DependencyEdge[] = [];

  for (const proc of model.processes) {
    const body = bodyForProc(model.processModels, proc);
    const subDeclScope = subprocessScopeFor(model, proc);
    const subModels = pairSubScope(subDeclScope, model.subprocessModels);
    // Names resolving to a SUBPROCESS (vs a process fallback), captured before
    // the fallbacks widen the map — tags each `do` edge's endpoint kind.
    const subprocessNames = new Set(subModels.keys());
    addProcessSpliceFallbacks(subModels, model, proc);
    const spliceIdentity = buildSpliceIdentity(subModels, subprocessNames);
    // §4.11 carve-out: a `do` target in ANOTHER space is a cross-space ENTRY
    // POINT (sanctioned when exported, flagged otherwise) whose internal steps
    // stay attributed to the OWNING space — the consumer process never absorbs
    // them (`in this` participation, transitive coupling). We keep the direct
    // `do` edge (the visible cross-space coupling) but drop every hop / nested
    // `do` edge emitted while inlining a cross-space target's body. Keyed by
    // the splice chain's OUTERMOST target (`chain[0]`), so descendants of a
    // carved splice are carved too. Single-space models compute an empty set.
    const carveTargets = crossSpaceSpliceTargets(proc, subModels, model.spaces);
    const carved = (chain: readonly string[]): boolean =>
      carveTargets.size > 0 && chain.length > 0 && carveTargets.has(chain[0]!);
    // The shared IR step-emitter owns the dense stepIndex + `do`-inlining; the
    // graph subscribes to call hops (module→interface edges) and resolved
    // splices (`do` edges) — notes/go/await still advance the index inside the
    // emitter, keeping edge indices aligned with the walker. Edges cover every
    // branch — `showAllBranches: true` (the Flow-view index space).
    emitSteps(body, { subModels, showAllBranches: true }, {
      step(u) {
        if (u.kind === "call" && u.hop) {
          if (carved(u.chain)) return; // cross-space splice internals: not the consumer's
          addProcessEdgeFromHop(proc, u.hop, u.stepIndex, u.altCondition, indexes, edges);
        }
      },
      splice(s) {
        if (carved(s.chain)) return; // nested `do` inside a cross-space target
        addDoEdgeFromSplice(proc, s, spliceIdentity, edges);
      },
    });
  }

  return edges;
}

/** §4.11 carve-out support: the subset of `subModels` keys (as the emitter
 *  resolves them — bare names AND module-scoped FQNs) whose target is declared
 *  in a DIFFERENT space than `proc`. A cross-space `do` is an entry point whose
 *  internals stay in the owning space; the graph drops those internals from the
 *  consumer's attribution. Empty for single-space (or package-root) models —
 *  both a space-less consumer and a space-less target yield no boundary. */
function crossSpaceSpliceTargets(
  proc: ProcessDecl,
  subModels: ReadonlyMap<string, { readonly source?: SourceSpan }>,
  spaces: ReadonlyMap<string, QualifiedName> | undefined
): ReadonlySet<string> {
  const out = new Set<string>();
  if (!spaces || spaces.size === 0) return out;
  const procFile = proc.source?.file;
  const procSpace = procFile ? spaces.get(procFile)?.join(".") : undefined;
  if (procSpace === undefined) return out;
  for (const [key, m] of subModels) {
    const tf = m.source?.file;
    const tSpace = tf ? spaces.get(tf)?.join(".") : undefined;
    if (tSpace !== undefined && tSpace !== procSpace) out.add(key);
  }
  return out;
}

/** Identity of a splice endpoint keyed by the name the emitter resolves through
 *  (the same key as `subModels`), so a `do` edge's `to` (target name) and `from`
 *  (enclosing chain element) both look up here. Process/subprocess identity
 *  mirrors `processIdentity` — a `#id` when present, else the qualified name. */
interface SpliceIdentity {
  readonly id: string;
  readonly name: string;
  readonly isSubprocess: boolean;
}

function buildSpliceIdentity(
  subModels: ReadonlyMap<string, { id?: StableId; name: string; ownerPath?: readonly string[] }>,
  subprocessNames: ReadonlySet<string>
): Map<string, SpliceIdentity> {
  const out = new Map<string, SpliceIdentity>();
  for (const [key, m] of subModels) {
    const qn = m.ownerPath && m.ownerPath.length > 0 ? [...m.ownerPath, m.name].join(".") : m.name;
    out.set(key, { id: m.id ?? qn, name: qn, isSubprocess: subprocessNames.has(key) });
  }
  return out;
}

/** Derive one `do` edge from a resolved splice: invoker process → spliced
 *  target. `from` is the enclosing chain's last element (or the root process at
 *  depth 0); attribution is always the ROOT process (splice-inclusive `in this`,
 *  design §1). */
function addDoEdgeFromSplice(
  proc: ProcessDecl,
  s: import("./process-step-emitter.js").EmitSplice,
  identity: ReadonlyMap<string, SpliceIdentity>,
  edges: DependencyEdge[]
): void {
  const to = identity.get(s.target);
  if (!to) return; // emitter only fires `splice` on a resolved target
  const from =
    s.chain.length > 0
      ? identity.get(s.chain[s.chain.length - 1]!)
      : { id: processIdentity(proc), name: processQualifiedName(proc), isSubprocess: false };
  if (!from) return;
  edges.push({
    kind: "do",
    fromProcessId: from.id,
    fromProcessName: from.name,
    toProcessId: to.id,
    toProcessName: to.name,
    targetIsSubprocess: to.isSubprocess,
    source: "process",
    processId: processIdentity(proc),
    processName: processQualifiedName(proc),
    stepIndex: s.stepIndex,
    ...(s.altCondition ? { altCondition: s.altCondition } : {}),
    span: s.node.targetSource ?? s.node.source,
  });
}

/** Pair a process declaration to its resolved IR body by source position
 *  (`file:offset`) — the declaration's span is identical on the AST and the IR,
 *  so it is a stable key (same approach validation uses). */
function bodyForProc(
  models: readonly ProcessModel[] | undefined,
  proc: ProcessDecl
): import("./process-model.js").Flow | undefined {
  const s = proc.source;
  if (!s) return undefined;
  const k = `${s.file}:${s.start.offset}`;
  for (const m of models ?? []) {
    const ms = m.source;
    if (ms && `${ms.file}:${ms.start.offset}` === k) return m.body;
  }
  return undefined;
}

/** Resolve the name-keyed subprocess SCOPE (lexical precedence, incl. process-
 *  nested) to IR `SubprocessModel` bodies by source position, so the emitter
 *  inlines `do` targets with the same scoping the AST graph used. */
function pairSubScope(
  declScope: ReadonlyMap<string, SubprocessDecl>,
  models: readonly SubprocessModel[] | undefined
): Map<string, SubprocessModel> {
  const bySource = new Map<string, SubprocessModel>();
  for (const m of models ?? []) {
    const s = m.source;
    if (s) bySource.set(`${s.file}:${s.start.offset}`, m);
  }
  const out = new Map<string, SubprocessModel>();
  for (const [name, decl] of declScope) {
    const s = decl.source;
    const m = s ? bySource.get(`${s.file}:${s.start.offset}`) : undefined;
    if (m) out.set(name, m);
  }
  return out;
}

/**
 * Subprocesses callable from `proc`, keyed by name. Mirrors the walker's
 * scoping (app-shell useProcessOrchestrator): top-level subprocesses are
 * global; module/type-scoped ones apply when their owner is a prefix of the
 * process owner; process-nested ones win last. Needed so `do X(...)` steps
 * can be inlined for edge derivation — a callee reached only through a
 * subprocess body would otherwise float disconnected.
 */
/**
 * v0.9.0 §2.5: `do` splices PROCESSES too — consulted only after the
 * subprocess walk misses (a same-name subprocess always wins), in the same
 * scope order (top-level global, module-scoped by owner-prefix, nearest
 * last). Processes have no `on Caller` param; validation warns on a
 * template-caller prefix.
 */
function addProcessSpliceFallbacks(
  subModels: Map<string, { readonly body: Flow }>,
  model: ResolvedModel,
  proc: ProcessDecl
): void {
  // v0.11: the shared space-aware scope builder (splice-scope.ts) — process
  // family (global FQNs). Subprocess-first precedence is preserved by the
  // `!has` guard (a same-key subprocess already claimed it).
  const named = (model.processModels ?? []).filter((pm) => !!pm.name) as (ProcessModel & {
    name: string;
  })[];
  const procScope = buildSpliceScope(named, proc);
  for (const [key, pm] of procScope) {
    if (!subModels.has(key)) subModels.set(key, pm);
  }
}

function subprocessScopeFor(
  model: ResolvedModel,
  proc: ProcessDecl
): Map<string, SubprocessDecl> {
  // v0.11: the shared space-aware scope builder — subprocess family.
  // Process-nested subprocesses win last, as before.
  const m = buildSpliceScope(model.subprocesses ?? [], proc);
  for (const sp of proc.subprocesses ?? []) m.set(sp.name, sp);
  return m;
}

// =============================================================================
// Index building
// =============================================================================

interface Indexes {
  modulesByPath: Map<string, ResolvedModule>;
  /** Reverse index: module → its qualified path (as readonly array) */
  pathOfModule: Map<ResolvedModule, readonly string[]>;
  interfacesByPath: Map<string, ResolvedInterface>;
  surfacesByPath: Map<string, ResolvedSurface>;
  modulesById: Map<StableId, ResolvedModule>;
  interfaceOwner: Map<ResolvedInterface, ResolvedModule>;
  /** Reverse index: interface → its FULL structural path (incl. surface
   *  segments). The edge derivation normalizes `toInterfacePath` through this so
   *  a hoisted/elided spelling and the full path are the SAME edge (§4.11 —
   *  identity is the structural path; diff/wire stay spelling-blind). */
  pathOfInterface: Map<ResolvedInterface, readonly string[]>;
  /** v0.9.0 §1.6: module key (any key of {@link modulesByPath}) → the leaf name
   *  of its default interface, when it declares one (a braced unnamed interface).
   *  A bare module callee (`A > B`, no trailing dot) resolves through this so it
   *  targets the default instead of a synthesized `(anonymous)` TODO leaf. */
  defaultInterfaceOf: Map<string, string>;
  /** v0.8.2 §4.11 space-scoping (present only in MULTI-space models). A written
   *  suffix → space-tagged module / interface-owner candidates, resolved against
   *  the referrer's space chain. Absent in single-space models (global is exact). */
  spaces?: ReadonlyMap<string, QualifiedName>;
  scopedModules?: Map<string, ScopedCand<ResolvedModule>[]>;
  scopedIfaceOwners?: Map<string, ScopedCand<ScopedIface>[]>;
}

/** A scoped interface candidate: the owning module + the interface itself, so the
 *  edge can resolve BOTH even when the global suffix index dropped an ambiguous
 *  key (a suffix ambiguous globally may be unique within a space). */
interface ScopedIface {
  readonly owner: ResolvedModule;
  readonly iface: ResolvedInterface;
}

/** Public alias for the structural index bundle (exported for incremental reuse). */
export type GraphIndexes = Indexes;

export function buildIndexes(model: ResolvedModel): Indexes {
  const modulesByPath = new Map<string, ResolvedModule>();
  const pathOfModule = new Map<ResolvedModule, readonly string[]>();
  const interfacesByPath = new Map<string, ResolvedInterface>();
  const surfacesByPath = new Map<string, ResolvedSurface>();
  const modulesById = new Map<StableId, ResolvedModule>();
  // v0.8.2: each interface → its enclosing MODULE, so an edge's target module is
  // the interface's TRUE owner (which may be nested/hoisted), not a re-walk of the
  // written prefix (which could match an unrelated same-leaf top-level module).
  const interfaceOwner = new Map<ResolvedInterface, ResolvedModule>();
  const pathOfInterface = new Map<ResolvedInterface, readonly string[]>();
  // Every interface with its owner path + owning-MODULE path (the §4.11 elision
  // boundary) — feeds the joint unique-spelling pass below.
  const ifaceEntries: IfaceEntry[] = [];

  // v0.8.2 §4.11: only MULTI-space models scope hoisting; single-space stays on
  // the exact global index (no per-referrer work, provably identical behavior).
  const spaces = model.spaces;
  const multiSpace = !!(spaces && spaces.size > 0);
  const scopedIfaceOwners = multiSpace
    ? new Map<string, ScopedCand<ScopedIface>[]>()
    : undefined;

  // v0.11 (D2a/D2b): the exact-path maps additionally accept the
  // space-qualified key (`Alpha::Api`, the D2 shape) for spaced modules.
  // The bare key stays (dual-keying) — it is what every existing consumer
  // (lsp/mcp/server) passes, and no legal pre-P6 model has a cross-space
  // collision, so first-write-wins bare aliases are exact. Alias removal is
  // a P6 line item.
  const aliasKey = (space: QualifiedName | undefined, key: string): string | null =>
    space && space.length > 0 ? `${space.join(".")}::${key}` : null;
  // v0.11 (D3): the space-EXTENDED written form (`Alpha.Api` — the space's
  // dotted name prefixed onto the structural path) is how a reference NAMES a
  // twin in another space. Registered as a fill-only alias: the unconditional
  // structural `set` above/below always wins the slot (exact structural path
  // beats an extended spelling — the D3 ladder law).
  //
  // Callers pass the space via `spaceOfModule`, NOT the raw `.space` stamp: the
  // membership sets in `hoist-index.ts` resolve it that way, and a model built
  // without the resolver's stamp (hand-assembled, or an older incremental
  // snapshot) would otherwise be admitted by membership but bind to nothing
  // here — the "validator says fine, graph resolves nothing" split these
  // indexes exist to prevent.
  const extendedKey = (space: QualifiedName | undefined, path: readonly string[]): string | null =>
    space && space.length > 0 ? [...space, ...path].join(".") : null;

  function visitModule(m: ResolvedModule, parent: readonly string[]): void {
    const path = [...parent, m.name];
    const key = path.join(".");
    modulesByPath.set(key, m);
    const spaced = aliasKey(m.space, key);
    if (spaced) modulesByPath.set(spaced, m);
    const extended = extendedKey(spaceOfModule(m, spaces), path);
    if (extended && !modulesByPath.has(extended)) modulesByPath.set(extended, m);
    pathOfModule.set(m, path);
    // Also accept the short name when unambiguous (top-level modules)
    if (parent.length === 0 && !modulesByPath.has(m.name)) {
      modulesByPath.set(m.name, m);
    }
    if (m.id) modulesById.set(m.id, m);

    for (const child of m.modules) visitModule(child, path);
    for (const surface of m.surfaces) visitSurface(surface, path, m);
    for (const iface of m.interfaces) visitInterface(iface, path, m);
  }

  function visitSurface(f: ResolvedSurface, parent: readonly string[], owner: ResolvedModule): void {
    const path = [...parent, f.name];
    const key = path.join(".");
    surfacesByPath.set(key, f);
    const spaced = aliasKey(owner.space, key);
    if (spaced) surfacesByPath.set(spaced, f);
    const extended = extendedKey(spaceOfModule(owner, spaces), path);
    if (extended && !surfacesByPath.has(extended)) surfacesByPath.set(extended, f);
    for (const sub of f.surfaces) visitSurface(sub, path, owner);
    for (const iface of f.interfaces) visitInterface(iface, path, owner);
  }

  function visitInterface(i: ResolvedInterface, parent: readonly string[], owner: ResolvedModule): void {
    const path = [...parent, i.name];
    const key = path.join(".");
    interfacesByPath.set(key, i);
    const spaced = aliasKey(owner.space, key);
    if (spaced) interfacesByPath.set(spaced, i);
    const extended = extendedKey(spaceOfModule(owner, spaces), path);
    if (extended && !interfacesByPath.has(extended)) interfacesByPath.set(extended, i);
    interfaceOwner.set(i, owner);
    pathOfInterface.set(i, path);
    const ownerModulePath = pathOfModule.get(owner) ?? [owner.name];
    ifaceEntries.push({ iface: i, ownerSegs: parent, moduleSegs: ownerModulePath });
    // Scoped index: every written spelling — owner path-suffix (`Auth.login`) or
    // §4.11 ELIDED module-prefix + leaf (`Orders.create` skipping the surface
    // segments) — → the owner MODULE, tagged with the owner's declaring space,
    // so the edge resolves the callee to a visible owner (§4.11). `parent` is
    // the iface's owner PATH incl. surfaces; the ScopedCand carries the owner
    // MODULE's path for the distinct-owner tie-break.
    if (scopedIfaceOwners) {
      const cand: ScopedCand<ScopedIface> = {
        value: { owner, iface: i },
        path: ownerModulePath,
        space: spaceOfModule(owner, spaces),
      };
      for (const key of ifaceSpellings(ownerModulePath, parent, i.name)) {
        const list = scopedIfaceOwners.get(key);
        if (list) list.push(cand);
        else scopedIfaceOwners.set(key, [cand]);
      }
    }
  }

  for (const m of model.modules) visitModule(m, []);

  // v0.8.2 hoisting: index each module by its UNIQUE proper path-suffixes so a
  // short/suffix module reference (`Auth`, `B.Auth`) resolves to the nested node.
  // An ambiguous suffix (2+ modules) is left unindexed — the edge orphans and the
  // validator flags AMBIGUOUS_MODULE_REF. An exact full-path key always wins.
  addUniqueModuleSuffixes(pathOfModule, modulesByPath);
  // Interfaces hoist by their owner's suffix + leaf (`Auth.login`) or resolve by
  // the §4.11 ELIDED spelling (`Orders.create` for a unique surface leaf) —
  // never the bare leaf alone (interfaces have no stable id), and only when the
  // spelling is unique across BOTH universes jointly (a cross-rule collision is
  // ambiguity, not a silent winner). Ladder step 1 (exact structural path wins):
  // a declared interface path is never overwritten, and a spelling that names an
  // exact MODULE path stays with the module — so a submodule keeps resolving via
  // its default interface (§4.13.2/§4.14.1) instead of being stolen by a
  // same-named surface leaf.
  addUniqueInterfaceSpellings(ifaceEntries, interfacesByPath, pathOfModule);

  // v0.9.0 §1.6: map every module key to its default interface's leaf, if it
  // declares one. Keyed off the completed `modulesByPath` (full paths + short
  // + unique suffixes) so a default resolves through the same names as the
  // module itself. First unnamed-braced interface wins; a second is a
  // validation error, not this index's concern.
  const defaultInterfaceOf = new Map<string, string>();
  for (const [key, m] of modulesByPath) {
    const def = m.interfaces.find((i) => i.decl.unnamed && i.decl.hasBody);
    if (def) defaultInterfaceOf.set(key, def.name);
  }

  return {
    modulesByPath,
    pathOfModule,
    interfacesByPath,
    surfacesByPath,
    modulesById,
    interfaceOwner,
    pathOfInterface,
    defaultInterfaceOf,
    ...(multiSpace
      ? { spaces, scopedModules: buildScopedModuleIndex(model), scopedIfaceOwners }
      : {}),
  };
}

/** One interface with the paths the spelling enumeration needs: its owner path
 *  (incl. surface segments) and its owning MODULE's path (the elision boundary). */
interface IfaceEntry {
  readonly iface: ResolvedInterface;
  readonly ownerSegs: readonly string[];
  readonly moduleSegs: readonly string[];
}

/** Add every UNIQUE non-exact interface spelling — owner path-suffixes plus the
 *  §4.11 elided (surface-skipping) forms, enumerated jointly via
 *  {@link ifaceSpellings} so a cross-rule collision counts as ambiguity (left
 *  unindexed → the edge orphans, the validator flags it). Exact structural paths
 *  always win: a declared interface key is never overwritten (`!byPath.has`),
 *  and a spelling equal to an exact MODULE path is skipped so the module ladder
 *  (default interface / anonymous handler) keeps claiming it. */
function addUniqueInterfaceSpellings(
  entries: readonly IfaceEntry[],
  byPath: Map<string, ResolvedInterface>,
  pathOfModule: ReadonlyMap<ResolvedModule, readonly string[]>
): void {
  const exactModulePaths = new Set<string>();
  for (const path of pathOfModule.values()) exactModulePaths.add(path.join("."));
  // Dedupe by full path first (duplicate declarations share one identity in
  // `byPath` — counting each copy would fabricate ambiguity), last one wins to
  // match the map's insertion semantics.
  const byFull = new Map<string, IfaceEntry>();
  for (const e of entries) byFull.set(`${e.ownerSegs.join(".")}.${e.iface.name}`, e);
  const count = new Map<string, number>();
  const one = new Map<string, ResolvedInterface>();
  for (const [full, { iface, ownerSegs, moduleSegs }] of byFull) {
    for (const spelling of ifaceSpellings(moduleSegs, ownerSegs, iface.name)) {
      if (spelling === full) continue; // the exact path is indexed directly
      count.set(spelling, (count.get(spelling) ?? 0) + 1);
      one.set(spelling, iface);
    }
  }
  for (const [key, iface] of one) {
    if (count.get(key) !== 1) continue;
    if (byPath.has(key)) continue; // exact interface path wins
    if (exactModulePaths.has(key)) continue; // exact module path wins (ladder 1)
    byPath.set(key, iface);
  }
}

/** Add unique proper path-suffixes of every module to `byPath` (v0.8.2 §4.11).
 *  Enumeration rule shared via {@link moduleSuffixes}; unique winners only. */
function addUniqueModuleSuffixes(
  pathOfModule: ReadonlyMap<ResolvedModule, readonly string[]>,
  byPath: Map<string, ResolvedModule>
): void {
  const count = new Map<string, number>();
  const one = new Map<string, ResolvedModule>();
  for (const [m, path] of pathOfModule) {
    for (const suf of moduleSuffixes(path, true)) {
      count.set(suf, (count.get(suf) ?? 0) + 1);
      one.set(suf, m);
    }
  }
  for (const [suf, m] of one) {
    if (count.get(suf) === 1 && !byPath.has(suf)) byPath.set(suf, m);
  }
}

function pathOf(m: ResolvedModule, indexes: Indexes): readonly string[] {
  return indexes.pathOfModule.get(m) ?? [m.name];
}

function ownerModuleOfInterface(
  ifacePath: string,
  indexes: Indexes
): ResolvedModule | undefined {
  // v0.8.2: if the interface resolves (possibly hoisted to a NESTED owner), use
  // its TRUE owning module — not a re-walk of the written prefix, which could hit
  // an unrelated same-leaf top-level module (`Auth.login` where a top-level `Auth`
  // shares the leaf but the nested `X.Auth` actually owns `login`).
  const iface = indexes.interfacesByPath.get(ifacePath);
  if (iface) {
    const owner = indexes.interfaceOwner.get(iface);
    if (owner) return owner;
  }
  // Fallback (unresolved / stub interface): walk up the written dotted path.
  const parts = ifacePath.split(".");
  for (let i = parts.length - 1; i >= 1; i--) {
    const candidate = parts.slice(0, i).join(".");
    const mod = indexes.modulesByPath.get(candidate);
    if (mod) return mod;
  }
  return undefined;
}

// =============================================================================
// Graph traversal helpers
// =============================================================================

/**
 * Modules that call into the given module — i.e., that have a process
 * step whose callee resolves into this module's surface. Returns each
 * unique caller once.
 */
export function getDependents(
  graph: DependencyGraph,
  target: ResolvedModule
): readonly ResolvedModule[] {
  const seen = new Set<ResolvedModule>();
  for (const edge of graph.edges) {
    if (edge.kind !== "call") continue; // module dependents ride call edges only
    const callee = graph.modulesByPath.get(edge.toModulePath.join("."));
    if (callee !== target) continue;
    const caller = graph.modulesByPath.get(edge.fromModulePath.join("."));
    if (caller && caller !== target) seen.add(caller);
  }
  return [...seen];
}

/**
 * Modules the given module depends on — i.e., whose interfaces it
 * calls into via process steps.
 */
export function getDependencies(
  graph: DependencyGraph,
  source: ResolvedModule
): readonly ResolvedModule[] {
  const seen = new Set<ResolvedModule>();
  for (const edge of graph.edges) {
    if (edge.kind !== "call") continue; // module dependencies ride call edges only
    const caller = graph.modulesByPath.get(edge.fromModulePath.join("."));
    if (caller !== source) continue;
    const callee = graph.modulesByPath.get(edge.toModulePath.join("."));
    if (callee && callee !== source) seen.add(callee);
  }
  return [...seen];
}

/**
 * Blast radius — every module reachable transitively from the given
 * module by following dependents (i.e., "if this breaks, what else
 * breaks"). Excludes the starting module itself.
 */
export function computeBlastRadius(
  graph: DependencyGraph,
  start: ResolvedModule
): readonly ResolvedModule[] {
  // Exclude the starting node from the result — the blast radius is "what
  // ELSE breaks if this fails", not the failing node itself.
  const visited = new Set<ResolvedModule>([start]);
  const queue: ResolvedModule[] = [start];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const dep of getDependents(graph, current)) {
      if (visited.has(dep)) continue;
      visited.add(dep);
      queue.push(dep);
    }
  }
  visited.delete(start);
  return [...visited];
}

// =============================================================================
// Internal: process step → edge
// =============================================================================

function addProcessEdgeFromHop(
  proc: ProcessDecl,
  hop: CallHop,
  stepIndex: number,
  altCondition: string | undefined,
  indexes: Indexes,
  edges: DependencyEdge[]
): void {
  // The callee interface path is the verdict the builder already computed via the
  // shared `classifyCallee` (the literal `to` for a declared leaf, or the
  // synthesized `module.(anonymous)` handler when left unspecified, §10.3) —
  // read it from the IR rather than re-classifying.
  const interfacePath = hop.callee.interfacePath;
  const ifaceKey = interfacePath.join(".");

  const fromKey = hop.caller.join(".");
  let fromModule = indexes.modulesByPath.get(fromKey);
  let targetIface = indexes.interfacesByPath.get(ifaceKey);
  let targetModule = ownerModuleOfInterface(ifaceKey, indexes);

  // v0.8.2 §4.11: in a MULTI-space model, a suffix ambiguous globally may be
  // unique among the modules VISIBLE to the referrer — resolve caller + callee
  // against the referrer's space chain, overriding the global (which would orphan
  // an ambiguous suffix). The referrer is the step's own file.
  if (indexes.scopedModules || indexes.scopedIfaceOwners) {
    const referrerFile = hop.callee.text.source?.file ?? proc.source?.file;
    const referrerSpace = referrerFile ? indexes.spaces?.get(referrerFile) : undefined;
    if (indexes.scopedModules) {
      const rc = resolveScoped(indexes.scopedModules.get(fromKey), referrerSpace);
      if (rc && rc !== "ambiguous") fromModule = rc.value;
    }
    if (indexes.scopedIfaceOwners) {
      const ro = resolveScoped(indexes.scopedIfaceOwners.get(ifaceKey), referrerSpace);
      if (ro && ro !== "ambiguous") {
        targetModule = ro.value.owner;
        targetIface = ro.value.iface; // proves a visible interface exists
      }
    }
  }

  // If validation has already flagged unresolvable references, skip;
  // otherwise emit a best-effort edge using the resolved interface path.
  const fromPath = fromModule ? pathOf(fromModule, indexes) : hop.caller.slice();
  const toModulePath = targetModule
    ? pathOf(targetModule, indexes)
    : interfacePath.slice(0, Math.max(1, interfacePath.length - 1));

  if (!targetIface) return; // can't form a meaningful edge without a callee

  // §4.11: the edge target is the interface's FULL structural path, not the
  // written spelling — a hoisted/elided reference and the full path are the SAME
  // dependency, so diff identity, wire output, and selector endpoint matching
  // stay spelling-blind (the resolver absorbs the elision). An unresolved/stub
  // target keeps the written path (its stub IS declared at that path).
  const toInterfacePath = indexes.pathOfInterface.get(targetIface) ?? interfacePath;

  edges.push({
    kind: "call",
    fromModulePath: fromPath,
    fromModuleId: fromModule?.id,
    toModulePath,
    toModuleId: targetModule?.id,
    toInterfacePath: toInterfacePath.slice(),
    // v0.11 (D2a): landed endpoint spaces (conditional — spaceless models
    // emit byte-identical edges).
    ...(fromModule?.space?.length ? { fromSpace: fromModule.space } : {}),
    ...(targetModule?.space?.length ? { toSpace: targetModule.space } : {}),
    source: "process",
    // Anonymous processes have no `id`; `processIdentity` gives each distinct
    // bare-step run its own stable key so their edges don't merge under one
    // empty identity (playback would otherwise highlight every anon process).
    processId: processIdentity(proc),
    processName: processQualifiedName(proc),
    stepIndex,
    altCondition,
    ...(altCondition === "unwind" ? { compensation: true } : {}),
    span: hop.callee.text.source,
  });
}
